import React, { useMemo } from 'react';
import { CreditCard, Tag, Package, Lock, Gift, Phone } from 'lucide-react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { computeGuestReservationPricing } from './guestReservationFormBuildPayload';
import { CHECKOUT_ZERO_TEST } from '../../lib/checkoutZeroTest';
import CloverPaymentForm from './CloverPaymentForm';
import { useGuestReservationForm } from './useGuestReservationForm';

export function GuestReservationFormExtrasAndPayment({
  m,
  section = 'all',
}: {
  m: ReturnType<typeof useGuestReservationForm>;
  /** Wizard: note = activity date + special requests; pay = hear about us / promo / pay; all = legacy. */
  section?: 'note' | 'pay' | 'all';
}) {
  const {
    t,
    onClose,
    itemType,
    itemId,
    itemPrice,
    taxeHebergementEnabled,
    formData,
    handleInputChange,
    handleCloverPayment,
    pendingReservationData,
    setPendingReservationData,
    paymentError,
    setPaymentError,
    isProcessingPayment,
    availabilityError,
    lockedUntil,
    rateLimitRetryAfterSec,
    vumPromo,
    couponFeedback,
    handleApplyCoupon,
  } = m;

  const allPackages = useQuery(api.packages.listAvailable) ?? [];

  const pricing = useMemo(
    () =>
      computeGuestReservationPricing(
        itemType,
        itemPrice,
        formData,
        vumPromo,
        taxeHebergementEnabled,
      ),
    [
      itemType,
      itemPrice,
      formData.checkIn,
      formData.checkOut,
      formData.duration,
      formData.adults,
      formData.children,
      vumPromo,
      taxeHebergementEnabled,
    ],
  );

  const totalPersons = (formData.adults ?? 1) + (formData.children ?? 0);
  const bookedNights = pricing.bookedNights;
  const isFullyCovered =
    !!vumPromo && itemType === 'accommodation' && bookedNights > 0 && bookedNights <= vumPromo.nights;
  const showNote = section === 'note' || section === 'all';
  const showPay = section === 'pay' || section === 'all';

  /**
   * Filter packages relevant to what the user is currently booking.
   * - accommodation → packages that include at least one stay product
   *   (ideally matching this specific accommodation, otherwise any stay)
   * - activity → packages that include this specific activity
   * - package → show all
   */
  const availablePackages = React.useMemo(() => {
    if (itemType === 'accommodation') {
      const exact = allPackages.filter((pkg) =>
        pkg.products.some((p) => p.type === 'stay' && p.productId === itemId)
      );
      if (exact.length > 0) return exact;
      return allPackages.filter((pkg) => pkg.products.some((p) => p.type === 'stay'));
    }
    if (itemType === 'activity') {
      const exact = allPackages.filter((pkg) =>
        pkg.products.some((p) => p.type === 'activity' && p.productId === itemId)
      );
      if (exact.length > 0) return exact;
      return allPackages.filter((pkg) => pkg.products.some((p) => p.type === 'activity'));
    }
    return allPackages;
  }, [allPackages, itemType, itemId]);

  /** Compute the representative discount % from a package's products. */
  const getPackageDiscount = (pkg: typeof allPackages[number]): number => {
    const discountValues = pkg.products
      .filter((p) => p.pricingType === 'discount' && typeof p.discountPercentage === 'number')
      .map((p) => p.discountPercentage as number);
    if (!discountValues.length) return 0;
    return Math.round(discountValues.reduce((a, b) => a + b, 0) / discountValues.length);
  };

  /** Build a short "includes" summary from a package's products. */
  const getPackageSummary = (pkg: typeof allPackages[number]): string => {
    const parts: string[] = [];
    const totalNights = pkg.products
      .filter((p) => p.type === 'stay')
      .reduce((sum, p) => sum + (p.stayNights ?? 0), 0);
    if (totalNights > 0) parts.push(`${totalNights} nuit${totalNights > 1 ? 's' : ''} chalet`);
    const activityCount = pkg.products.filter((p) => p.type === 'activity').length;
    if (activityCount > 0) parts.push(`${activityCount} activité${activityCount > 1 ? 's' : ''}`);
    const foodCount = pkg.products.filter((p) => p.type === 'food').length;
    if (foodCount > 0) parts.push(`${foodCount} repas`);
    return parts.join(' + ');
  };

  return (
    <>
            {showNote && itemType === 'activity' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('guestReservation.activityDetails')}
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('guestReservation.preferredDate')} *
                  </label>
                  <input
                    type="date"
                    name="checkIn"
                    autoComplete="off"
                    value={formData.checkIn}
                    onChange={handleInputChange}
                    required={showNote}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                  />
                </div>
              </div>
            )}

            {showNote ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('guestReservation.specialRequests')}
              </label>
              <textarea
                name="specialRequests"
                autoComplete="off"
                value={formData.specialRequests}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                placeholder={t('guestReservation.specialRequestsPlaceholder')}
              />
            </div>
            ) : null}

            {showPay ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comment avez-vous entendu parler de nous ? *
              </label>
              <select
                name="hearAboutUs"
                value={formData.hearAboutUs}
                onChange={handleInputChange}
                required={showPay}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500 bg-white ${!formData.hearAboutUs ? 'border-gray-300' : 'border-nature-400'}`}
              >
                <option value="">— Sélectionner —</option>
                <option value="facebook_instagram">Facebook / Instagram</option>
                <option value="google">Google</option>
                <option value="bouche_a_oreille">Bouche-à-oreille (amis / famille)</option>
                <option value="vum">VUM / Forfait cadeau</option>
                <option value="publicite">Publicité / Flyer</option>
                <option value="deja_venu">Déjà venu(e)</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            ) : null}

            {/* Coupon Code — hidden when a VUM promo is already active */}
            {showPay && !vumPromo && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('guestReservation.couponCode')}
                </h3>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      name="couponCode"
                      autoComplete="off"
                      value={formData.couponCode}
                      onChange={handleInputChange}
                      placeholder={t('guestReservation.couponCodePlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nature-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyCoupon}
                    className="px-4 py-2 bg-nature-600 text-white rounded-md hover:bg-nature-700 transition-colors flex items-center"
                  >
                    <Tag className="w-4 h-4 mr-2" />
                    {t('guestReservation.applyCoupon')}
                  </button>
                </div>
                {couponFeedback && (
                  <p className="mt-1 text-sm text-nature-700" role="status">
                    {couponFeedback}
                  </p>
                )}
              </div>
            )}

            {/* Promo-code contact banner + pay step rest */}
            {showPay ? (
            <>
            <div className="flex items-start gap-4 p-4 rounded-xl border border-amber-200 bg-amber-50">
              <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                <Phone className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-amber-900 text-sm">
                  {t('guestReservation.promoContactTitle')}
                </p>
                <p className="text-amber-800 text-xs mt-0.5 leading-relaxed">
                  {t('guestReservation.promoContactDesc')}
                </p>
                <a
                  href="mailto:info@pignonrouge.ca"
                  className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2 transition-colors"
                >
                  <Phone className="w-3.5 h-3.5" />
                  {t('guestReservation.promoContactCta')}
                </a>
              </div>
            </div>

            {/* Package Recommendations — pulled from live Convex packages */}
            {availablePackages.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {t('guestReservation.recommendations')}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {t('guestReservation.recommendationsDescription')}
                </p>
                <div className="space-y-3">
                  {availablePackages.map((pkg) => {
                    const discount = getPackageDiscount(pkg);
                    const summary = getPackageSummary(pkg);
                    return (
                      <div key={pkg._id} className="flex items-start justify-between gap-3 p-4 bg-white rounded-xl border border-nature-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start space-x-3 min-w-0">
                          <div className="shrink-0 w-9 h-9 rounded-lg bg-nature-100 flex items-center justify-center">
                            <Gift className="w-5 h-5 text-nature-600" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 truncate">{pkg.name}</div>
                            <div className="text-sm text-gray-500 mt-0.5">{pkg.description}</div>
                            {summary && (
                              <div className="text-xs text-nature-700 font-medium mt-1">
                                {t('guestReservation.packageIncludes')} : {summary}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="text-sm font-semibold text-gray-800">{pkg.price} $ CAD</span>
                              {discount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                                  -{discount}% {t('guestReservation.discount')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="shrink-0 px-3 py-1.5 bg-nature-600 text-white rounded-lg hover:bg-nature-700 transition-colors text-sm font-medium whitespace-nowrap"
                        >
                          {t('guestReservation.addToBooking')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Payment — full amount only (no 30% deposit on quick reservation) */}
            {(() => {
              if (isFullyCovered) return null;

              return (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('guestReservation.paymentMethod')}
                  </h3>
                  {CHECKOUT_ZERO_TEST && (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      Mode test — paiement à 0&nbsp;$. Le courriel de confirmation sera quand même envoyé.
                    </div>
                  )}
                  <div className="flex items-start gap-3 rounded-xl border-2 border-nature-500 bg-nature-50 p-4">
                    <CreditCard className="mt-0.5 h-4 w-4 shrink-0 text-nature-700" />
                    <div>
                      <div className="font-semibold text-gray-900">Paiement au complet (100%)</div>
                      <p className="mt-0.5 text-sm text-gray-500">Le montant total est débité maintenant.</p>
                    </div>
                  </div>

                  {pendingReservationData && (
                    <div className="mt-4">
                      <CloverPaymentForm
                        totalAmount={pricing.totalWithTax}
                        onTokenReady={() => {}}
                        onSubmit={handleCloverPayment}
                        isProcessing={isProcessingPayment}
                      />
                      {paymentError && (
                        <p className="mt-2 text-sm text-red-600">{paymentError}</p>
                      )}
                      <button
                        type="button"
                        onClick={() => { setPendingReservationData(null); setPaymentError(null); }}
                        className="mt-2 text-sm text-gray-500 underline"
                      >
                        Modifier les informations
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Total with tax breakdown */}
            {(() => {
              const extraNights = pricing.extraNights;
              const includedNights = pricing.includedNights;
              const exceedsCapacity = vumPromo && totalPersons > vumPromo.maxPersons;
              const { basePrice, taxeHebergement: taxeHeb, gst, qst, totalWithTax } = pricing;

              return (
                <>
                  <div className="bg-nature-50 rounded-lg p-4 space-y-2 text-sm">
                    {/* Subtotal row */}
                    <div className="flex justify-between text-gray-700">
                      <span>
                        {vumPromo && itemType === 'accommodation' && bookedNights > 0 ? (
                          includedNights > 0 && (
                            <span className="line-through text-gray-400 mr-2">
                              ${itemPrice} × {includedNights} nuit{includedNights > 1 ? 's' : ''}
                            </span>
                          )
                        ) : null}
                        {itemType === 'accommodation' && bookedNights > 0
                          ? `${vumPromo ? extraNights : bookedNights} nuit${(vumPromo ? extraNights : bookedNights) > 1 ? 's' : ''} × ${itemPrice} $`
                          : itemType === 'activity'
                          ? `${totalPersons} personne${totalPersons > 1 ? 's' : ''} × ${itemPrice} $`
                          : 'Sous-total'}
                      </span>
                      <span className="font-medium">{isFullyCovered ? <span className="text-emerald-600 font-semibold">Forfait inclus</span> : `${basePrice.toFixed(2)} $`}</span>
                    </div>

                    {!isFullyCovered && (
                      <>
                        {itemType === 'accommodation' && taxeHeb > 0 && (
                          <div className="flex justify-between text-gray-500">
                            <span>{t('common.taxLodgingLabel')}</span>
                            <span>{taxeHeb.toFixed(2)} $</span>
                          </div>
                        )}
                        <div className="flex justify-between text-gray-500">
                          <span>{t('common.taxGstLabel')}</span>
                          <span>{gst.toFixed(2)} $</span>
                        </div>
                        <div className="flex justify-between text-gray-500">
                          <span>{t('common.taxQstLabel')}</span>
                          <span>{qst.toFixed(2)} $</span>
                        </div>
                        <p className="text-xs text-gray-400 pt-0.5">{t('common.checkoutSalesTaxNote')}</p>
                        <div className="border-t border-nature-200 pt-2 mt-1 flex justify-between items-center">
                          <span className="text-base font-semibold text-gray-900">Total (taxes incluses)</span>
                          <span className="text-xl font-bold text-nature-600">{totalWithTax.toFixed(2)} $</span>
                        </div>
                      </>
                    )}
                  </div>

                  {exceedsCapacity && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
                      <span className="text-lg shrink-0">⚠️</span>
                      <div>
                        <p className="font-semibold">Capacité dépassée</p>
                        <p>Votre forfait VUM est valide pour un maximum de <strong>{vumPromo!.maxPersons} personnes</strong>. Vous avez sélectionné {totalPersons} participants. Veuillez réduire le nombre de participants pour continuer.</p>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Availability error banner */}
            {availabilityError && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
                <Lock className="w-5 h-5 mt-0.5 shrink-0 text-red-600" />
                <div>
                  {availabilityError === 'booked' ? (
                    <p className="font-medium">Ces dates sont déjà réservées.</p>
                  ) : availabilityError === 'rate_limited' ? (
                    <p className="font-medium">
                      Trop de tentatives de verrouillage. Réessayez
                      {rateLimitRetryAfterSec != null ? ` dans ${rateLimitRetryAfterSec} s` : ' dans un instant'}.
                    </p>
                  ) : (
                    <>
                      <p className="font-medium">Ces dates sont temporairement réservées par un autre client.</p>
                      {lockedUntil && (
                        <p className="text-xs mt-1 text-red-600">
                          Disponible à nouveau vers {new Date(lockedUntil).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </>
                  )}
                  <p className="text-xs mt-1 text-red-600">Veuillez choisir d'autres dates.</p>
                </div>
              </div>
            )}

            {/* Submit Button — shown on pay step; wizard parent may also provide Next/Back */}
            {!pendingReservationData && (
              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  disabled={!!availabilityError || !!(vumPromo && ((formData.adults ?? 1) + (formData.children ?? 0)) > vumPromo.maxPersons)}
                  className={`w-full px-6 py-3 rounded-lg transition-colors text-white ${
                    availabilityError
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-nature-600 hover:bg-nature-700'
                  }`}
                >
                  {t('guestReservation.submitReservation')}
                </button>
              </div>
            )}
            </>
            ) : null}
    </>
  );
}

